import { Box, Button, Flex, Grid, IconButton, SimpleGrid, Skeleton, Text, VStack } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { RiDeleteBinLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { deleteRedoRequestRequest } from "../../../modules/admin-modules/redo-requests/Actions";
import { brandPalette } from "../../../constants/brandPalette";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
  canvas: brandPalette.warmBeige,
  muted: "rgba(69, 11, 17, 0.65)",
};

const PER_PAGE = 10;

const RedoRequestTable = ({ searchTerm = "" }) => {
  const redoRequests = useSelector((state) => state.redoRequests.redoRequests);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.redoRequests.isLoading);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = Array.isArray(redoRequests) ? redoRequests : [];
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return list;
    return list.filter((row) => {
      const exam = String(row?.exam?.name || "").toLowerCase();
      const student = String(row?.student?.name || "").toLowerCase();
      return exam.includes(q) || student.includes(q);
    });
  }, [redoRequests, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const sliceStart = (safePage - 1) * PER_PAGE;
  const pageRows = filtered.slice(sliceStart, sliceStart + PER_PAGE);

  if (isLoading) {
    return (
      <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} height="160px" borderRadius="16px" startColor="gray.200" endColor="gray.300" />
          ))}
        </SimpleGrid>
      </Box>
    );
  }

  if (!filtered.length) {
    return (
      <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
        <Box
          border="1px dashed"
          borderColor={C.border}
          borderRadius="16px"
          bg="var(--surface-canvas)"
          py={16}
          textAlign="center"
        >
          <Text fontWeight="800" color={C.primary} fontSize="lg">
            {searchTerm ? "No requests match your search" : "No exam redo requests"}
          </Text>
          <Text mt={2} color={C.muted} fontSize="sm" maxW="md" mx="auto">
            When students request a retake, they will appear here.
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6}>
        {pageRows.map((data) => (
          <VStack
            key={data.id}
            align="stretch"
            spacing={4}
            border="1px solid"
            borderColor={C.border}
            borderRadius="16px"
            p={5}
            bg="var(--surface-raised)"
            boxShadow="0 4px 18px rgba(69, 11, 17, 0.06)"
            transition="box-shadow 0.2s, border-color 0.2s"
            _hover={{
              boxShadow: "0 10px 28px rgba(69, 11, 17, 0.1)",
              borderColor: "rgba(91, 13, 22, 0.35)",
            }}
          >
            <Box minW={0}>
              <Text fontSize="xs" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em">
                Exam
              </Text>
              <Text fontSize="lg" fontWeight="800" color={C.secondary} mt={1} noOfLines={2}>
                {data?.exam?.name || "—"}
              </Text>
            </Box>

            <Box pt={1} borderTop="1px solid" borderColor="rgba(216, 206, 195, 0.6)">
              <Text fontSize="xs" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em">
                Student
              </Text>
              <Text fontSize="sm" fontWeight="700" color={C.secondary} mt={1} noOfLines={2}>
                {data?.student?.name || "—"}
              </Text>
            </Box>

            <Flex justify="flex-end" pt={1}>
              <IconButton
                aria-label="Delete request"
                icon={<RiDeleteBinLine />}
                variant="outline"
                borderColor={C.border}
                color="red.600"
                borderRadius="full"
                _hover={{ bg: "rgba(254, 178, 178, 0.25)", borderColor: "red.400" }}
                onClick={() => dispatch(deleteRedoRequestRequest(data?.id))}
              />
            </Flex>
          </VStack>
        ))}
      </Grid>

      {totalPages > 1 ? (
        <Flex mt={8} align="center" justify="center" gap={4} flexWrap="wrap">
          <Button
            size="sm"
            borderRadius="full"
            variant="outline"
            borderColor={C.border}
            color={C.primary}
            isDisabled={safePage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <Text fontSize="sm" fontWeight="700" color={C.secondary}>
            Page {safePage} of {totalPages}
          </Text>
          <Button
            size="sm"
            borderRadius="full"
            variant="outline"
            borderColor={C.border}
            color={C.primary}
            isDisabled={safePage >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </Button>
        </Flex>
      ) : null}
    </Box>
  );
};

export default RedoRequestTable;
